import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const sizeCls = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
  }[size];

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4 backdrop-blur-sm" onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        className={cn(
          'relative w-full rounded-xl border border-slate-200 bg-white shadow-card dark:border-ink-700 dark:bg-ink-800',
          sizeCls,
        )}
        role="dialog" aria-modal="true" aria-labelledby={title ? 'dialog-title' : undefined}
      >
        <button
          onClick={onClose}
          aria-label="Fermer"
          className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-md text-ink-400 hover:bg-slate-100 hover:text-ink-700 dark:hover:bg-ink-700 dark:hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
        {title && (
          <div className="border-b border-slate-200 px-6 py-4 dark:border-ink-700">
            <h2 id="dialog-title" className="font-display text-lg text-ink-900 dark:text-white">{title}</h2>
            {description && <p className="mt-1 text-sm text-ink-500">{description}</p>}
          </div>
        )}
        <div className="px-6 py-5">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-3 dark:border-ink-700 dark:bg-ink-900/50">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
